"use client";

import Link from 'next/link';
import { Github, Twitter, Instagram, Youtube } from 'lucide-react';
import { useState, useEffect } from 'react';
import Image from 'next/image';

// Custom SVG components for Discord and Telegram
const DiscordIcon = (props: React.SVGProps<SVGSVGElement>) => (
    <svg role="img" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor" {...props}>
        <title>Discord</title>
        <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8852-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4464.8253-.618 1.2525a17.7663 17.7663 0 00-5.4844 0c-.1716-.4272-.407-1.0225-.618-1.2525a.0741.0741 0 00-.0785-.0371a19.7913 19.7913 0 00-4.8852 1.5152.069.069 0 00-.0321.027c-1.6112 3.1934-2.2813 6.46-2.0945 9.69a18.0673 18.0673 0 003.3842 4.6258.0742.0742 0 00.0862.0163c.3636-.172.6936-.381.9954-.627a.074.074 0 00.021-.0815c-.0982-.164-.1882-.335-.2703-.5136a.0722.0722 0 00-.044-.0448c-1.1528-.4446-2.0945-1.22-2.76-2.1317a.0754.0754 0 01.006-.1023c.333-.245.6432-.505.922-.779a.0722.0722 0 01.0843-.012c4.1309 1.604 8.7424 1.604 12.8733 0a.0722.0722 0 01.0843.012c.2788.274.5892.534.922.779a.0754.0754 0 01.006.1023c-.6655.9117-1.6072 1.687-2.76 2.1317a.0722.0722 0 00-.044.0448c-.0821.1786-.1721.3496-.2703-.5136a.074.074 0 00.021.0815c.3018.246.6318.455.9954.627a.0742.0742 0 00.0862-.0163a18.0673 18.0673 0 003.3842-4.6258c.1868-3.23-.4834-6.4966-2.0945-9.69a.069.069 0 00-.0321-.027zm-5.4212 6.5625c-1.0225 0-1.8545-1.01-1.8545-2.25s.832-2.25 1.8545-2.25s1.8545 1.01 1.8545 2.25s-.832 2.25-1.8545 2.25zm4.842 0c-1.0225 0-1.8545-1.01-1.8545-2.25s.832-2.25 1.8545-2.25s1.8545 1.01 1.8545 2.25s-.832 2.25-1.8545 2.25z" />
    </svg>
);

const TelegramIcon = (props: React.SVGProps<SVGSVGElement>) => (
    <svg role="img" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor" {...props}>
        <title>Telegram</title>
        <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.17.91-.494 1.208-.822 1.23-.696.04-1.22-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91s5.273-4.876 5.344-5.3a.23.23 0 0 0-.05-.19c-.073-.06-.154-.04-.22-.02-.102.04-1.725 1.14-4.93 3.32-1.012.7-1.923 1.023-2.67 1.002-.78-.022-1.39-.244-1.91-.41-.57-.18-1.01-.27-1.01-.52 0-.322.39-.494.943-.667.974-.306 2.536-.832 3.656-1.23 2.94-1.043 5.05-1.706 5.33-1.8.3-.1.5-.2.58-.2z" />
    </svg>
);

const Footer = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  useEffect(() => {
    // Ensure the year is updated on the client-side to avoid hydration mismatches.
    setYear(new Date().getFullYear());
  }, []);

  return (
    <footer className="bg-card border-t border-border/50">
      <div className="container mx-auto px-4 md:px-6 py-4">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <Link href="/" className="flex items-center">
            <Image src="/logotrans.png" alt="CryptoSI Logo" width={150} height={40} />
          </Link>
          <p className="text-sm text-foreground font-medium">
            © {year} CryptoSI. All Rights Reserved.
          </p>
          <div className="flex flex-wrap items-center gap-4 justify-center text-sm text-foreground">
            <Link href="https://x.com/Crypto_SI" aria-label="Twitter" target="_blank" rel="noopener noreferrer">
              <Twitter className="h-5 w-5 text-social-twitter hover:opacity-80 transition-opacity" />
            </Link>
            <Link href="https://www.instagram.com/cryptosi.eth/" aria-label="Instagram" target="_blank" rel="noopener noreferrer">
              <Instagram className="h-5 w-5 text-social-instagram hover:opacity-80 transition-opacity" />
            </Link>
            <Link href="https://www.youtube.com/@smartreach5326" aria-label="YouTube" target="_blank" rel="noopener noreferrer">
              <Youtube className="h-5 w-5 text-social-youtube hover:opacity-80 transition-opacity" />
            </Link>
            <Link href="https://github.com/Crypto-SI" aria-label="GitHub" target="_blank" rel="noopener noreferrer">
              <Github className="h-5 w-5 text-foreground hover:opacity-80 transition-opacity" />
            </Link>
            <Link href="https://t.me/+AVoimeTI8Ew5ZGQ0" aria-label="Telegram" target="_blank" rel="noopener noreferrer">
              <TelegramIcon className="h-5 w-5 text-social-telegram hover:opacity-80 transition-opacity" />
            </Link>
            <Link href="https://discord.gg/G3qFyvYuBt" aria-label="Discord" target="_blank" rel="noopener noreferrer">
              <DiscordIcon className="h-5 w-5 text-social-discord hover:opacity-80 transition-opacity" />
            </Link>
            <div className="flex items-center gap-2 text-xs text-foreground font-semibold">
              <span>Built with love by</span>
              <Link
                href="https://webarastudio.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center hover:opacity-90 transition-opacity"
                aria-label="Visit Webara Studio"
              >
                <Image
                  src="/webarabadge.webp"
                  alt="Webara Studio Badge"
                  width={80}
                  height={24}
                />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
